// document.ts

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { CommonModule } from '@angular/common';

import { ActivatedRoute, Router } from '@angular/router';

import { FormsModule } from '@angular/forms';

import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { LeadService } from '../shared/service/leads.service';
import { CraditService } from '../../credit/shared/services/cradit.service';
import { MODULE_SEQ, PKL_GRP } from '../../../core/shared/models/picklist.model';

import { DynamicFormComponent } from '../../../core/shared/dynamic-form/dynamic-form.component';

import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';

import { TableAction, TableColumn } from '../../shared/models/table-models';

import { Table } from '../../shared/table/table';

import { BackButton } from '../../shared/buttons/back-button';
import { Setup } from '../../setup/shared/service/setup';
import { Popup } from '../../../core/shared/components/popup/popup';
import { DocumentHistory } from '../document-history/document-history';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { PickListService } from '../../../core/shared/services/picklist.service';
import {
  FO_LTR_DOC_SEQ,
  FO_LTR_ISSUE_DOC_DELETABLE_STATUSES,
  buildFoLtrSignedCopyFormData,
  FO_LTR_ROLES,
  FO_LTR_STATUS,
  FO_LTR_STATUS_LABEL_KEYS,
  hasFoLtrRole,
} from '../../credit/shared/models/fo-ltr-status.model';

/** Permission flags returned with the documents by GET /crdt-prpsl/fo-ltr/get-fo-ltr-doc. */
export interface FoLtrDocFlags {
  canUpload: boolean;
  uploadableDocSeqs: number[];
  canSubmit: boolean;
}

@Component({
  selector: 'app-document',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Table,
    DynamicFormComponent,
    BackButton,
    TranslatePipe,
    Popup,
    DocumentHistory,
  ],
  templateUrl: './document.html',
  styleUrl: './document.css',
})
export class Document implements OnInit, OnChanges {
  @ViewChild(DynamicFormComponent, { static: false })
  dynamicForm!: DynamicFormComponent;

  private cleanBase64(value: string): string {
    if (!value) {
      return '';
    }

    let base64 = String(value).trim();

    // Complete data URL prefix remove karega.
    const commaIndex = base64.indexOf(',');

    if (base64.startsWith('data:') && commaIndex !== -1) {
      base64 = base64.substring(commaIndex + 1);
    }

    // Quotes, spaces, new lines remove
    return base64.replace(/^["']|["']$/g, '').replace(/\s/g, '');
  }

  /** Extension -> MIME, for names that already say what the file is. */
  private static readonly EXT_MIME: Record<string, string> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    csv: 'text/csv',
    txt: 'text/plain',
    zip: 'application/zip',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
  };

  /**
   * Works out what a stored document actually is.
   *
   * `fileName` wins when it carries a known extension — the file said what it
   * was on the way in, and no amount of sniffing beats that. Otherwise the
   * bytes are inspected.
   *
   * Office formats need more than a magic number: .docx, .xlsx and .pptx are
   * all ZIP containers starting "PK", and .doc, .xls and .ppt are all OLE2
   * compound files starting D0 CF 11 E0. Guessing from the first four bytes
   * hands Excel a .docx and Word an .xls, so the container is looked into —
   * ZIP entry names ("xl/", "word/", "ppt/") and OLE2 stream names, both of
   * which sit in plain sight near the start of the file.
   */
  private getFileInfo(
    base64Value: string,
    fileName = '',
  ): {
    mimeType: string;
    extension: string;
  } {
    const named = (fileName.match(/\.([A-Za-z0-9]+)$/)?.[1] ?? '').toLowerCase();

    if (named && Document.EXT_MIME[named]) {
      return { mimeType: Document.EXT_MIME[named], extension: named };
    }

    const base64 = this.cleanBase64(base64Value);

    if (!base64) {
      return { mimeType: 'application/octet-stream', extension: 'bin' };
    }

    try {
      // Enough to reach the container's entry/stream names, not just its magic
      // number. Trimmed to a multiple of 4 so atob gets whole base64 groups.
      const sampleLength = Math.min(base64.length, 8192) & ~3;
      const binary = atob(base64.substring(0, sampleLength));
      const bytes = Array.from(binary).map((char) => char.charCodeAt(0));

      // JPEG: FF D8 FF
      if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
        return { mimeType: 'image/jpeg', extension: 'jpg' };
      }

      // PNG: 89 50 4E 47 0D 0A 1A 0A
      if (
        bytes[0] === 0x89 &&
        bytes[1] === 0x50 &&
        bytes[2] === 0x4e &&
        bytes[3] === 0x47 &&
        bytes[4] === 0x0d &&
        bytes[5] === 0x0a &&
        bytes[6] === 0x1a &&
        bytes[7] === 0x0a
      ) {
        return { mimeType: 'image/png', extension: 'png' };
      }

      // PDF: %PDF
      if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
        return { mimeType: 'application/pdf', extension: 'pdf' };
      }

      // GIF: GIF87a / GIF89a
      if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
        return { mimeType: 'image/gif', extension: 'gif' };
      }

      // WEBP: RIFF....WEBP
      if (
        bytes[0] === 0x52 &&
        bytes[1] === 0x49 &&
        bytes[2] === 0x46 &&
        bytes[3] === 0x46 &&
        bytes[8] === 0x57 &&
        bytes[9] === 0x45 &&
        bytes[10] === 0x42 &&
        bytes[11] === 0x50
      ) {
        return { mimeType: 'image/webp', extension: 'webp' };
      }

      // OLE2 compound file: D0 CF 11 E0 — .doc, .xls or .ppt.
      if (bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0) {
        return this.detectOle2(binary);
      }

      // ZIP container: PK — .docx, .xlsx, .pptx or a plain archive.
      if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
        return this.detectOoxml(binary);
      }
    } catch (error) {
      console.error('File type detection failed:', error);
    }

    return { mimeType: 'application/octet-stream', extension: 'bin' };
  }

  /**
   * Which OOXML format a ZIP holds, from the part names in its entry headers.
   * Those names are stored uncompressed, so they read straight out of the raw
   * bytes without unzipping anything.
   */
  private detectOoxml(binary: string): { mimeType: string; extension: string } {
    if (binary.includes('word/')) {
      return { mimeType: Document.EXT_MIME['docx'], extension: 'docx' };
    }

    if (binary.includes('xl/')) {
      return { mimeType: Document.EXT_MIME['xlsx'], extension: 'xlsx' };
    }

    if (binary.includes('ppt/')) {
      return { mimeType: Document.EXT_MIME['pptx'], extension: 'pptx' };
    }

    return { mimeType: Document.EXT_MIME['zip'], extension: 'zip' };
  }

  /**
   * Which legacy Office format an OLE2 file holds, from its stream names.
   * Those are stored as UTF-16, so the ASCII letters sit between NUL bytes —
   * stripping them is enough to match on.
   */
  private detectOle2(binary: string): { mimeType: string; extension: string } {
    const names = binary.split(String.fromCharCode(0)).join('');

    if (names.includes('WordDocument')) {
      return { mimeType: Document.EXT_MIME['doc'], extension: 'doc' };
    }

    if (names.includes('Workbook') || names.includes('Book')) {
      return { mimeType: Document.EXT_MIME['xls'], extension: 'xls' };
    }

    if (names.includes('PowerPoint')) {
      return { mimeType: Document.EXT_MIME['ppt'], extension: 'ppt' };
    }

    // Unknown OLE2 payload — Word is the commonest, and was the old default.
    return { mimeType: Document.EXT_MIME['doc'], extension: 'doc' };
  }

  private getFileNameWithoutExtension(fileName: string): string {
    return (fileName || 'document').replace(/\.[^/.]+$/, '');
  }

  private getImageMimeType(base64: string): string {
    const cleanBase64 = this.cleanBase64(base64);

    if (cleanBase64.startsWith('/9j/')) {
      return 'image/jpeg';
    }

    if (cleanBase64.startsWith('iVBORw0KGgo')) {
      return 'image/png';
    }

    if (cleanBase64.startsWith('R0lGOD')) {
      return 'image/gif';
    }

    if (cleanBase64.startsWith('UklGR')) {
      return 'image/webp';
    }
    // Old Microsoft Word .doc
    if (cleanBase64.startsWith('0M8R4KGxGuE')) {
      return 'application/msword';
    }

    // DOCX, XLSX, PPTX are ZIP-based files
    if (
      cleanBase64.startsWith('UEsDB') ||
      cleanBase64.startsWith('UEsBA') ||
      cleanBase64.startsWith('UEsFB')
    ) {
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    }
    return 'application/octet-stream';
  }

  private getFileExtension(mimeType: string): string {
    const extensionMap: Record<string, string> = {
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'application/pdf': 'pdf',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    };

    return extensionMap[mimeType] || 'bin';
  }

  // =====================================
  // INPUTS
  // =====================================

  @Input() moduleSeq!: number;

  @Input() entitySeq!: number;

  /**
   * Lists the documents of several entities at once, merged into one table —
   * used by screens whose documents hang off child records rather than the
   * record on screen (e.g. Monitoring, where report files attach to each
   * `mntrngSeq`, not to the client). When set it takes precedence over
   * `entitySeq`; leaving it empty keeps the single-entity behaviour every
   * other screen relies on, untouched.
   */
  @Input() entitySeqs: number[] = [];

  /**
   * Value for the upload's `docDt` field, `yyyy-MM-dd`.
   *
   * Screens where the document belongs to a dated event (a monitoring report
   * covers one monitoring date) pass it in. Left empty the field is not sent,
   * so existing callers are unaffected.
   */
  @Input() docDt = '';

  /**
   * Shows a "Visit Date" column sourced from each document's own `docDt`
   * (the monitoring report upload sends `docDt` in the payload, and the list
   * API returns it back on each row). Only the Monitoring Report tab sets
   * this — every other screen embedding this component leaves it off and
   * keeps its current columns unchanged.
   */
  @Input() showVisitDateColumn = false;

  /**
   * `accept` for the file picker. Defaults to what every existing screen
   * offered (images, PDF, Word) so those are unchanged; screens that take a
   * different set — monitoring reports are Excel or Word — pass their own.
   */
  @Input() accept =
    'image/*,application/pdf,application/msword,' +
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
    '.pdf,.doc,.docx';

  /**
   * Extensions actually allowed, lower-case and without the dot.
   *
   * `accept` only filters the picker's default view — "All files" walks
   * straight past it — so when this is set the choice is checked as well.
   * Empty means accept whatever the picker returned, as before.
   */
  @Input() allowedExtensions: string[] = [];

  /**
   * Filters the upload form's "Document Type" dropdown by each type's
   * `issuDtReqFlg` (issuance-date-required) flag — used only by the Facility
   * Offer Letter Documents page's two tabs: `true` for its "Required
   * Documents" tab (issuance-dated types only), `false` for its "All
   * Documents" tab (everything except issuance-dated types). Defaults to
   * `null` (no filtering) so every other screen embedding this component —
   * leads, onboarding, and anywhere else that never sets this input — keeps
   * showing every document type, untouched.
   */
  @Input() issuanceDocsOnly: boolean | null = null;

  /**
   * Restricts the upload form's "Document Type" dropdown to these `docSeq`
   * values. Used by the Facility Offer Letter list's documents popup, which
   * limits an Account Manager (LNLY_AM) to the two types they may upload.
   * Empty means no restriction, so every other screen embedding this
   * component keeps showing every document type, untouched.
   */
  @Input() allowedDocSeqs: number[] = [];

  /**
   * When the header Upload's "Document Type" dropdown has a single option,
   * pre-select it and disable the select. Used by the Facility Offer Letter
   * list's documents popup for the Account Manager (LNLY_AM).
   */
  @Input() lockSingleDocType = false;

  /**
   * Removes these `docSeq` values from the upload form's "Document Type"
   * dropdown. Used by the Facility Offer Letter Documents page, which hides
   * the two Account Manager types from a Relationship Officer (LNLY_RO).
   * Empty means no exclusion, so every other screen keeps the full dropdown.
   */
  @Input() excludedDocSeqs: number[] = [];

  /**
   * Limits the "Document Type" dropdown to types not uploaded yet, plus types
   * whose upload was rejected (so they can be uploaded again). A type that
   * has any non-rejected row in this list drops out. Off by default, so other
   * screens keep the full dropdown.
   */
  @Input() hideUploadedDocTypes = false;

  /**
   * Hides everything that uploads a document — the header's "Upload Document"
   * button and the Required Documents checklist's per-row upload icon. The
   * Facility Offer Letter Documents page sets it so only a Relationship
   * Officer (LNLY_RO) can upload; every other screen leaves it true and keeps
   * uploading exactly as before.
   */
  @Input() canUpload = true;

  /**
   * Adds the Soft Copy (required) / Hard Copy (optional) date fields to the
   * upload form, sent as the save-doc payload's `sftCpyDt` and `hrdCpyDt`.
   * Only the Facility Offer Letter Documents page's two tabs set it — the
   * offer-letter list's popup, leads and onboarding never do, so their form
   * and payload stay exactly as they were.
   */
  @Input() showFoLtrDateFields = false;


  /**
   * Restricts the listed documents to these document types (`docTyp`). The
   * Facility Offer Letter list's popup shows only the offer letter itself
   * (36). Empty means no filtering, so every other screen keeps listing
   * every document it loads.
   */
  @Input() visibleDocSeqs: number[] = [];

  /**
   * Adds an "Upload Date" field to the upload form, sent as the save-doc
   * payload's `uploadDt`. Only the Facility Offer Letter list's documents
   * popup sets it; everywhere else (including the standalone Facility Offer
   * Letter Documents page) `uploadDt` stays today's date and no field shows.
   */
  @Input() showUploadDateField = false;

  /**
   * When true, the upload form posts to `POST /crdt-prpsl/fo-ltr/save-doc`
   * (the Facility Offer Letter document endpoint) instead of the default
   * `POST /leads/upload-lead-docs`. Used only by the Facility Offer Letter
   * Documents page's two tabs. Defaults to false so every other screen
   * embedding this component — leads, onboarding, and the offer-letter
   * checklist popup on the Facility Offer Letter list — keeps uploading
   * through the leads endpoint, untouched.
   */
  @Input() useFoLtrSaveApi = false;

  /**
   * Emits the uploaded documents after every (re)load so a parent can gate
   * its own flow — e.g. only allow submit once every required document is in.
   */
  @Output() documentsChange = new EventEmitter<any[]>();

  /**
   * Emits the permission flags returned alongside the documents by
   * get-fo-ltr-doc (`canUpload`, `uploadableDocSeqs`, `canSubmit`), so the
   * offer-letter popup can gate its submit button on the backend's decision.
   */
  @Output() foLtrDocFlagsChange = new EventEmitter<FoLtrDocFlags>();

  /** Emits after a Facility Offer Letter document is saved or deleted (save-doc succeeded). */
  @Output() foLtrDocSaved = new EventEmitter<void>();

  /**
   * Forces the read-only presentation: list and download only, no upload,
   * edit or delete. Screens that embed this component purely for reference
   * set it; `?mode=view` on the route still works on its own, so leaving it
   * unset keeps the existing behaviour exactly.
   */
  @Input() viewOnly = false;


  /**
   * Marks the component as embedded inside a popup, which drops the actions
   * that navigate away: the header's Back button and the row's View History
   * link. Both would leave the page sitting behind the modal, with the modal
   * gone and nothing to come back to.
   */
  @Input() inPopup = false;

  /**
   * Shows the row's Download action icon. The Credit Proposal's Documents
   * tab turns it off; every other screen keeps it, so their actions are
   * unchanged. The document name link still downloads the file.
   */
  @Input() showDownloadAction = true;

  /**
   * Adds an eye (View History) action that shows the document's history in a
   * popup, through the same history API the /document-history page uses.
   * Only the Credit Proposal's Documents tab sets it; every other screen
   * keeps its existing actions.
   */
  @Input() showHistoryPopupAction = false;

  /**
   * Shows the Delete action as the Font Awesome trash icon used on the Setup
   * screens (e.g. Common Codes) instead of the image icon. Only the Credit
   * Proposal's Documents tab sets it; every other screen keeps its icon.
   */
  @Input() useFaDeleteIcon = false;

  /**
   * Runs when a document's Download is clicked. Resolving `true` means the
   * screen handled the download itself (e.g. the Facility Offer Letter
   * screen generates the letter from its parameters), so the stored file is
   * not downloaded. Unset by default, so other screens download as before.
   */
  @Input() beforeDownload: ((row: any) => Promise<boolean>) | null = null;

  /**
   * Facility Offer Letter screens only: the header's Upload button follows
   * get-fo-ltr-doc's `canUpload`, and the type dropdown its
   * `uploadableDocSeqs` (when the backend sends any). Off by default.
   */
  @Input() foLtrUploadGate = false;

  /**
   * With `foLtrUploadGate`: instead of hiding the Upload button while the
   * backend says `canUpload: false`, keep it visible and show this message
   * (a translation key) when it is clicked. Empty keeps the button hidden.
   */
  @Input() foLtrUploadBlockedMessage = '';

  /**
   * Facility Offer Letter screens only: uploading one of these document
   * types when a row of that type already exists re-uploads that row (its
   * crdtFoLtrDocSeq goes with the save) instead of adding a new one — e.g.
   * the printed copy of the offer letter (36) replaces the draft.
   */
  @Input() replaceExistingDocSeqs: number[] = [];

  /**
   * Facility Offer Letter screens only: the Account Manager (LNLY_AM) gets
   * the Delete action (within the FOL delete rules) and, on issue
   * documents, the Update Hard Copy Date action.
   */
  @Input() foLtrAmActions = false;

  /** get-fo-ltr-doc `canUpload` / `uploadableDocSeqs`, kept for `foLtrUploadGate`. */
  foLtrCanUpload = false;
  private foLtrUploadableDocSeqs: number[] = [];

  /** Header Upload button: `canUpload`, plus the backend's say on FOL screens that gate it. */
  get showHeaderUpload(): boolean {
    return (
      this.canUpload &&
      !this.isViewMode &&
      !this.showRequiredDocChecklist &&
      (!this.foLtrUploadGate || this.foLtrCanUpload || !!this.foLtrUploadBlockedMessage)
    );
  }

  /** Header Upload button click — explains why when the backend doesn't allow an upload yet. */
  onHeaderUploadClick(): void {
    if (this.foLtrUploadGate && !this.foLtrCanUpload && this.foLtrUploadBlockedMessage) {
      this.toastr.warning(this.translate.instant(this.foLtrUploadBlockedMessage));
      return;
    }

    this.openUploadModal();
  }

  showHistoryPopup = false;

  /** Row whose history the popup shows. */
  historyRow: { entySeq: number; docTyp: number } | null = null;

  isViewMode = false;

  /** Read-only because the route said so, kept apart from the input. */
  private routeViewMode = false;

  // =====================================
  // STATE
  // =====================================

  editingDocSeq: number | null = null;

  showUploadModal = false;

  previewUrl: string | null = null;

  selectedFile: File | null = null;

  fileError = '';

  compressingFile = false;

  /** Uploaded documents cannot exceed 25MB. */
  private readonly maxFileSizeBytes = 25 * 1024 * 1024;

  /** Images larger than this on their longest side are downscaled before upload. */
  private readonly maxImageDimension = 1920;

  // =====================================
  // DATA
  // =====================================

  documents: any[] = [];
  showDeletePopup = false;
  deleteMessage = '';
  selectedRow: any = null;

  docFormValue: any = {};

  docTypeOptions: any[] = [];

  docFields: DynamicFieldConfig[] = [];

  // =====================================
  // TABLE
  // =====================================

  columns: TableColumn[] = [];
  buildColumns(): void {
    this.columns = [
      {
        key: 'docNm',
        label: this.translate.instant('DOCUMENT.DOCUMENT_NAME'),
        type: 'link',
        onClick: (row: any) => this.downloadFile(row),
      },
      {
        key: 'docTypLabel',
        label: this.translate.instant('DOCUMENT.DOCUMENT_TYPE'),
      },
      ...(this.showVisitDateColumn
        ? [{ key: 'visitDateLabel', label: 'DOCUMENT.VISIT_DATE' } as TableColumn]
        : []),
      // Facility Offer Letter "All Documents" tab shows Status (ref code
      // groups 23/26) instead of Updated On — leads/onboarding, which never
      // set `useFoLtrSaveApi`, keep Updated On exactly as before.
      this.useFoLtrSaveApi
        ? { key: 'statusLabel', label: 'COMMON.STATUS' }
        : {
            key: 'updatedDate',
            // Monitoring Report tab wants "Document Update Date" instead of
            // the default label — every other screen keeps "Updated On".
            label: this.showVisitDateColumn
              ? 'DOCUMENT.DOCUMENT_UPDATE_DATE'
              : this.translate.instant('DOCUMENT.UPDATED_ON'),
          },
    ];
  }

  actions: TableAction[] = [
    {
      icon: 'images/download.png',
      tooltip: 'COMMON.DOWNLOAD',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full',
      onClick: (row: any) => this.onDownloadClick(row),
      iconSize: 'sm',
      visible: () => this.showDownloadAction,
    },
    {
      // Same eye icon and style as the Onboarding list's View action.
      icon: 'images/view-more.png',
      class: 'view',
      tooltip: 'DOCUMENT.VIEW_HISTORY',
      onClick: (row: any) => this.openHistoryPopup(row),
      iconSize: 'w-6 h-6',
      visible: () => this.showHistoryPopupAction,
    },
    {
      icon: 'images/view-more.png',
      tooltip: 'DOCUMENT.VIEW_HISTORY',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full',
      onClick: (row: any) => this.viewDocumentHistory(row),
      iconSize: 'sm',
      visible: () => !this.inPopup,
    },
    {
      icon: 'images/del-icon.png',
      tooltip: 'COMMON.DELETE',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-red-100 rounded-full',
      onClick: (row: any) => this.openDeletePopup(row),
      iconSize: 'sm',
      // Facility Offer Letter screens (useFoLtrSaveApi) never show delete —
      // leads, onboarding, and every other screen embedding this component
      // never set that input, so they keep delete exactly as before.
      // Facility Offer Letter screens (useFoLtrSaveApi) show delete only to
      // the Account Manager, within the FOL delete rules — leads, onboarding,
      // and every other screen keep delete exactly as before.
      visible: (row: any) =>
        !this.useFaDeleteIcon &&
        !this.isViewMode &&
        !this.isCreditManager &&
        (!this.useFoLtrSaveApi || this.canDeleteFoLtrDocument(row)),
    },
    {
      // Same trash icon and colour as the Setup screens' Delete action.
      icon: faTrash,
      class: 'text-red-600',
      tooltip: 'COMMON.DELETE',
      onClick: (row: any) => this.openDeletePopup(row),
      visible: (row: any) =>
        this.useFaDeleteIcon &&
        !this.isViewMode &&
        !this.isCreditManager &&
        (!this.useFoLtrSaveApi || this.canDeleteFoLtrDocument(row)),
    },
    {
      icon: 'images/calendar.png',
      tooltip: 'DOCUMENT.UPDATE_HARD_COPY_DATE',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full',
      onClick: (row: any) => this.openHardCopyDatePopup(row),
      iconSize: 'sm',
      visible: (row: any) => this.canUpdateHardCopyDate(row),
    },
  ];

  // =====================================
  // REQUIRED DOCUMENT CHECKLIST (Facility Offer Letter "Required Documents"
  // tab only — one row per required, issuance-dated document type, with an
  // Upload action, instead of the usual "already uploaded" documents table)
  // =====================================

  requiredDocChecklist: any[] = [];

  requiredDocChecklistColumns: TableColumn[] = [
    {
      key: 'docNm',
      label: 'DOCUMENT.DOCUMENT_TYPE',
      type: 'link',
      onClick: (row: any) => this.downloadFile(row),
    },
    { key: 'statusLabel', label: 'COMMON.STATUS' },
  ];

  requiredDocChecklistActions: TableAction[] = [
    {
      icon: 'images/download.png',
      tooltip: 'COMMON.DOWNLOAD',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full',
      iconSize: 'sm',
      onClick: (row: any) => this.downloadFile(row),
      // Nothing uploaded yet against this required type — no file to download.
      visible: (row: any) => !!row.docImg,
    },
    {
      icon: 'images/view-more.png',
      tooltip: 'DOCUMENT.VIEW_HISTORY',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full',
      iconSize: 'sm',
      onClick: (row: any) => this.viewDocumentHistory(row),
    },
    {
      icon: 'images/upload.png',
      tooltip: 'COMMON.UPLOAD',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full',
      iconSize: 'sm',
      onClick: (row: any) => this.openUploadModal(row.docTyp, row.crdtFoLtrDocSeq, row),
      // FOL new flow: only the Account Manager (LNLY_AM) uploads issue
      // documents — the first upload, or again after a SAC rejection (765).
      visible: (row: any) =>
        this.canUpload && !this.isViewMode && this.isAccountManager && this.isIssueDocUploadable(row),
    },
    {
      // 4813 (RO approved – signed copy pending): AM uploads the signed copy
      // — a new row (sgndCpyFlg=1) linked to this one via prntDocSeq.
      icon: 'images/upload.png',
      tooltip: 'FACILITIES.UPLOAD_SIGNED_COPY',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-green-100 rounded-full',
      iconSize: 'sm',
      onClick: (row: any) => this.openUploadModal(row.docTyp, null, row, 'signedCopy'),
      visible: (row: any) =>
        this.canUpload &&
        !this.isViewMode &&
        this.isAccountManager &&
        Number(row?.stsKey) === FO_LTR_STATUS.RO_APPROVED_SIGNED_COPY_PENDING,
    },
    {
      // 4814 (RO recommendation – pending AM): AM re-uploads the same row
      // (a signed copy stays a signed copy).
      icon: 'images/upload.png',
      tooltip: 'FACILITIES.REUPLOAD_DOCUMENT',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-yellow-100 rounded-full',
      iconSize: 'sm',
      onClick: (row: any) => this.openUploadModal(row.docTyp, row.crdtFoLtrDocSeq, row, 'reupload'),
      visible: (row: any) =>
        this.canUpload &&
        !this.isViewMode &&
        this.isAccountManager &&
        Number(row?.stsKey) === FO_LTR_STATUS.RO_RECOMMENDATION_PENDING_AM,
    },
    {
      icon: 'images/calendar.png',
      tooltip: 'DOCUMENT.UPDATE_HARD_COPY_DATE',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full',
      iconSize: 'sm',
      onClick: (row: any) => this.openHardCopyDatePopup(row),
      visible: (row: any) => this.canUpdateHardCopyDate(row),
    },
  ];

  // Facility Offer Letter "Required Documents" tab shows the checklist table
  // (one row per required type + Upload action) instead of the usual
  // already-uploaded documents table.
  get showRequiredDocChecklist(): boolean {
    return this.useFoLtrSaveApi && this.issuanceDocsOnly === true;
  }
  /**
   * Required Documents checklist: one row per issue-flag document type —
   * GET /setup/get-all-docs/13 filtered to `issuDtReqFlg: true` (and not
   * deleted) — so every required type shows even before anything has been
   * uploaded. Each row then picks up its latest uploaded document, if any,
   * from get-fo-ltr-issu-doc and get-fo-ltr-doc (status, file, dates, seq).
   */
  private loadRequiredDocChecklist(): void {
    if (!this.showRequiredDocChecklist || !this.entitySeq) {
      return;
    }

    const asList = (res: any): any[] =>
      Array.isArray(res)
        ? res
        : Array.isArray(res?.docs)
          ? res.docs
          : Array.isArray(res?.data)
            ? res.data
            : [];

    forkJoin({
      types: this.setup.getAllDocumentByCategry(this.moduleSeq),
      issued: this.creditService.getFoLtrIssuanceDocs(this.entitySeq).pipe(catchError(() => of([]))),
      uploaded: this.creditService.getFoLtrDocuments(this.entitySeq).pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ types, issued, uploaded }) => {
        // Uploaded rows of this letter, by document type; the latest row wins.
        const latestByType = new Map<number, any>();

        [...asList(issued), ...asList(uploaded)]
          .filter((item: any) => !item?.delFlg && item?.crdtFoLtrDocSeq != null)
          .forEach((item: any) => {
            const type = Number(item.docSeq);
            const current = latestByType.get(type);

            if (!current || Number(item.crdtFoLtrDocSeq) > Number(current.crdtFoLtrDocSeq)) {
              latestByType.set(type, item);
            } else if (Number(item.crdtFoLtrDocSeq) === Number(current.crdtFoLtrDocSeq)) {
              // Same row from both endpoints — keep whichever fields either one has
              // (a null from one endpoint never wipes the other's value).
              const merged = { ...current };

              Object.keys(item).forEach((key) => {
                if (merged[key] == null && item[key] != null) {
                  merged[key] = item[key];
                }
              });

              latestByType.set(type, merged);
            }
          });

        this.requiredDocChecklist = asList(types)
          .filter((type: any) => !type?.delFlg && type?.issuDtReqFlg === true)
          .map((type: any) => {
            const docTyp = Number(type.docSeq);
            const item = latestByType.get(docTyp);

            return {
              // `docSeq` follows the same convention as the uploaded-documents
              // table (mapDocRow): it's the uploaded document instance's own
              // seq. `docTyp` is the document *type*.
              docSeq: item?.crdtFoLtrDocSeq ?? null,
              docTyp,
              docNm: type.docNm || this.getDocumentTypeLabel(docTyp),
              crdtFoLtrDocSeq: item?.crdtFoLtrDocSeq ?? null,
              stsKey: item?.stsKey ?? null,
              statusLabel: item ? this.getFoLtrStatusLabel(item.stsKey) : '-',
              docImg: item?.docImg ?? null,
              sgndCpyFlg: item?.sgndCpyFlg ?? null,
              chrgTyp: item?.chrgTyp ?? null,
              signingDt: item?.signingDt ?? null,
              // Carried so reopening the upload popup shows the dates already
              // recorded against this document instead of blank fields.
              sftCpyDt: item?.sftCpyDt ?? null,
              hrdCpyDt: item?.hrdCpyDt ?? null,
              // Upload flags from get-fo-ltr-issu-doc.
              canReupload: item?.canReupload === true,
              canUploadSgndCpy: item?.canUploadSgndCpy === true,
              chrgTypReq: item?.chrgTypReq === true,
            };
          });
      },

      error: () => {
        this.requiredDocChecklist = [];
        this.toastr.error(this.translate.instant('DOCUMENT.FAILED_TO_LOAD_REQUIRED_DOCUMENTS'));
      },
    });
  }

  get isRelationshipOfficer(): boolean {
    return (
      (localStorage.getItem('role') || localStorage.getItem('user_role') || '')
        .trim()
        .toUpperCase() === 'LNLY_RO'
    );
  }

  get isAccountManager(): boolean {
    return hasFoLtrRole(FO_LTR_ROLES.AM);
  }

  /**
   * Issue document row (Financial Documents) the AM can upload: nothing
   * uploaded yet, or rejected by SAC. "Uploaded" means the row has a
   * document seq — the list APIs don't always send the file (`docImg`), so
   * that can't be relied on (it showed the first-upload icon on rows that
   * were already uploaded, next to the signed-copy icon).
   */
  private isIssueDocUploadable(row: any): boolean {
    const uploaded = row?.crdtFoLtrDocSeq != null && Number(row.crdtFoLtrDocSeq) > 0;

    return !uploaded || Number(row?.stsKey) === FO_LTR_STATUS.REJECTED;
  }

  /** Issue-flag document types (setup `issuDtReqFlg`) of this module, from the full type list. */
  private issueDocTypes = new Set<number>();

  private isIssueDocType(docTyp: any): boolean {
    return this.issueDocTypes.has(Number(docTyp));
  }

  /**
   * FOL delete rules (save-doc delFlg=1): the offer letter document (36)
   * never; issue documents only while 4812 / 2658 / 4815; any other
   * document at any status. Account Manager only.
   */
  canDeleteFoLtrDocument(row: any): boolean {
    if (!this.useFoLtrSaveApi || !this.foLtrAmActions || !this.isAccountManager || this.isViewMode) {
      return false;
    }

    if (Number(row?.docTyp) === FO_LTR_DOC_SEQ) {
      return false;
    }

    if (this.isIssueDocType(row?.docTyp)) {
      return FO_LTR_ISSUE_DOC_DELETABLE_STATUSES.includes(Number(row?.stsKey));
    }

    return true;
  }

  /** update-hrd-cpy-dt: the AM may set an uploaded issue document's hard copy date at any time. */
  canUpdateHardCopyDate(row: any): boolean {
    return (
      this.useFoLtrSaveApi &&
      this.foLtrAmActions &&
      this.isAccountManager &&
      !this.isViewMode &&
      !!(row?.crdtFoLtrDocSeq ?? row?.docSeq) &&
      this.isIssueDocType(row?.docTyp)
    );
  }

  // =====================================
  // UPDATE HARD COPY DATE (FOL issue documents — LNLY_AM)
  // =====================================

  showHardCopyDatePopup = false;
  hardCopyDateFields: DynamicFieldConfig[] = [
    {
      key: 'hrdCpyDt',
      label: 'DOCUMENT.HARD_COPY_DATE',
      type: 'date',
      // Facility Offer Letter only: past and current dates, no future ones.
      validations: { required: true, noFuturDate: true },
    },
  ];
  hardCopyDateFormData: any = {};
  private hardCopyDateDocSeq: number | null = null;

  openHardCopyDatePopup(row: any): void {
    const crdtFoLtrDocSeq = Number(row?.crdtFoLtrDocSeq ?? row?.docSeq ?? 0);

    if (crdtFoLtrDocSeq <= 0) {
      this.toastr.warning(this.translate.instant('DOCUMENT.DOCUMENT_INFORMATION_MISSING'));
      return;
    }

    this.hardCopyDateDocSeq = crdtFoLtrDocSeq;
    this.hardCopyDateFormData = { hrdCpyDt: row?.hrdCpyDt ?? null };
    this.showHardCopyDatePopup = true;
  }

  /** POST /crdt-prpsl/fo-ltr/update-hrd-cpy-dt */
  handleHardCopyDateSave = (form: any) => {
    const crdtFoLtrDocSeq = Number(this.hardCopyDateDocSeq ?? 0);

    if (crdtFoLtrDocSeq <= 0 || !form?.hrdCpyDt) {
      return false;
    }

    return this.creditService
      .updateFoLtrHardCopyDate({ crdtFoLtrDocSeq, hrdCpyDt: form.hrdCpyDt })
      .subscribe({
        next: (res: any) => {
          if (res?.__hasError) {
            return;
          }

          if (res?.error) {
            this.toastr.error(res.error);
            return;
          }

          this.toastr.success(this.translate.instant('DOCUMENT.HARD_COPY_DATE_UPDATED'));
          this.hardCopyDateDocSeq = null;
          this.loadDocuments();
        },

        error: (err: any) => {
          this.toastr.error(
            err?.error?.message ??
              err?.error?.error ??
              this.translate.instant('DOCUMENT.UNABLE_TO_UPDATE_HARD_COPY_DATE'),
          );
        },
      });
  };

  get isCreditManager(): boolean {
    return (
      (localStorage.getItem('role') || localStorage.getItem('user_role') || '')
        .trim()
        .toUpperCase() === 'LNLY_CM'
    );
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private leadService: LeadService,
    private toastr: ToastrService,
    private setup: Setup,
    private translate: TranslateService,
    private creditService: CraditService,
    private cdr: ChangeDetectorRef,
    private pickListService: PickListService,
  ) {}
  openHistoryPopup(row: any): void {
    const entySeq = Number(row?.entySeq) || this.entitySeq;

    if (!entySeq || !row?.docTyp) {
      this.toastr.warning(this.translate.instant('DOCUMENT.DOCUMENT_INFORMATION_MISSING'));
      return;
    }

    this.historyRow = { entySeq, docTyp: Number(row.docTyp) };
    this.showHistoryPopup = true;
  }

  viewDocumentHistory(row: any): void {
    if ((!this.entitySeq && !row?.entySeq) || !row?.docTyp) {
      this.toastr.warning(this.translate.instant('DOCUMENT.DOCUMENT_INFORMATION_MISSING'));
      return;
    }

    this.router.navigate(['/document-history', Number(row.entySeq) || this.entitySeq], {
      queryParams: {
        docType: row.docTyp,
        entyTyp: this.moduleSeq,
        // Facility Offer Letter documents keep their history in their own
        // table, so the history page must hit get-fo-ltr-doc-hstry instead of
        // the leads endpoint. Every other screen never sets `useFoLtrSaveApi`
        // and so keeps the leads history call untouched.
        ...(this.useFoLtrSaveApi ? { source: 'foLtr' } : {}),
      },
    });
  }
  // =====================================
  // INIT
  // =====================================

  ngOnInit(): void {
    this.buildColumns();

    this.translate.onLangChange.subscribe(() => {
      this.buildColumns();
      this.buildDocFields();
    });

    this.route.queryParamMap.subscribe((q) => {
      this.routeViewMode = q.get('mode') === 'view';
      this.isViewMode = this.viewOnly || this.routeViewMode;
    });

    if (this.useFoLtrSaveApi) {
      // Facility Offer Letter statuses (ref code group 50), with the older
      // groups as fallbacks. Labels are worked out when the rows load, so
      // they are refreshed once the picklist is actually in the store.
      this.pickListService
        .loadCodesAsync([
          PKL_GRP.FOL_STATUS,
          PKL_GRP.ONBOARDING_STATUS,
          PKL_GRP.PANDDING_STATUS,
          PKL_GRP.CHARGE_TYPE,
        ])
        .subscribe({
          next: () => this.refreshFoLtrStatusLabels(),
          error: (error: any) => console.error('Unable to load FOL status picklist:', error),
        });
    }

    this.loadDocTypes();
    this.loadDocuments();
  }

  // The parent rebuilds `entitySeqs` whenever its own rows reload, so the
  // merged list has to follow it — without this the table keeps showing the
  // documents of whichever records happened to be loaded first.
  ngOnChanges(changes: SimpleChanges): void {
    /*
     * Recomputed on every change, not just at init: a host that turns editing
     * on once its entity is known (pick a monitoring record, then upload)
     * would otherwise stay stuck in the read-only state it was created in.
     */
    if (changes['viewOnly']) {
      this.isViewMode = this.viewOnly || this.routeViewMode;
    }

    const many = changes['entitySeqs'];
    const one = changes['entitySeq'];

    if (many && !many.firstChange) {
      const previous: number[] = many.previousValue ?? [];
      const current: number[] = many.currentValue ?? [];

      if (previous.join(',') !== current.join(',')) {
        this.loadDocuments();
        return;
      }
    }

    /*
     * A single-entity host that swaps entity in place needs the same reload.
     * Skipped when `entitySeqs` drives the list, since the entity then only
     * decides where an upload goes, not what is shown.
     */
    if (one && !one.firstChange && !this.entitySeqs.length) {
      if (Number(one.previousValue) !== Number(one.currentValue)) {
        this.loadDocuments();
      }
    }
  }

  // =====================================
  // LOAD DOC TYPES
  // =====================================

  loadDocTypes(): void {
    this.setup.getAllDocumentByCategry(this.moduleSeq).subscribe({
      next: (res: any[]) => {
        const list = Array.isArray(res) ? res : [];

        // The dropdown may be filtered (allowed/excluded types), but the
        // table's "Document Type" column still has to label rows of a type
        // the current user may not upload — so labels come from the full list.
        this.docTypeLabels = new Map(
          list.map((item: any) => [Number(item.docSeq), item.docNm as string]),
        );

        this.issueDocTypes = new Set(
          list.filter((item: any) => !!item.issuDtReqFlg).map((item: any) => Number(item.docSeq)),
        );

        this.docTypeOptions = list
          .filter((item: any) => {
            if (this.allowedDocSeqs.length && !this.allowedDocSeqs.includes(Number(item.docSeq))) {
              return false;
            }

            if (this.excludedDocSeqs.includes(Number(item.docSeq))) {
              return false;
            }

            if (this.issuanceDocsOnly === true) return !!item.issuDtReqFlg;
            if (this.issuanceDocsOnly === false) return !item.issuDtReqFlg;
            return true;
          })
          .map((item: any) => ({
            label: item.docNm,

            value: item.docSeq,

            issuDtReqFlg: !!item.issuDtReqFlg,
          }));

        this.buildDocFields();
      },

      error: () => {
        this.toastr.error(this.translate.instant('DOCUMENT.FAILED_TO_LOAD_DOCUMENT_TYPES'));
      },
    });
  }

  // =====================================
  // BUILD FIELDS
  // =====================================

  buildDocFields(): void {
    this.docFields = [
      {
        key: 'docTyp',
        label: this.translate.instant('DOCUMENT.DOCUMENT_TYPE'),
        type: 'select',
        options$: of(this.getUploadDocTypeOptions()),
        // Read-only when the type comes from a Required Documents row.
        disabled: this.lockedDocTyp != null,
        validations: {
          required: true,
        },
      },
      {
        key: 'docNm',
        label: this.translate.instant('DOCUMENT.DOCUMENT_NAME'),
        type: 'text',
        disabled: true,
      },
    ];

    this.docFields.push({
      key: 'docDate',
      // The Facility Offer Letter save-doc API calls this field `aprvngDt`
      // (Approving Date) rather than an issuance date — every other screen
      // embedding this component (leads, onboarding) keeps the normal
      // "Issue Date" label since they never set `useFoLtrSaveApi`.
      // save-doc rejects the request without it, so it stays on every tab.
      label: this.useFoLtrSaveApi
        ? 'DOCUMENT.APPROVING_DATE'
        : this.translate.instant('DOCUMENT.ISSUE_DATE'),
      type: 'date',
      hidden: true,
    });

    // Soft Copy (required) / Hard Copy (optional) dates — Facility Offer
    // Letter Documents page only (both of its tabs). The approving date is
    // set by the backend (today).
    if (this.showFoLtrDateFields) {
      this.docFields.push(
        // Past and current dates only (no future dates) — Facility Offer Letter.
        {
          key: 'sftCpyDt',
          label: 'DOCUMENT.SOFT_COPY_DATE',
          type: 'date',
          validations: { required: true, noFuturDate: true },
        },
        {
          key: 'hrdCpyDt',
          label: 'DOCUMENT.HARD_COPY_DATE',
          type: 'date',
          validations: { noFuturDate: true },
        },
      );
    }

    // "Upload Date" is picked by the user only on the Facility Offer Letter
    // list's documents popup — every other screen leaves this off and keeps
    // sending today's date.
    // Signed copy (scanned final document): the agreement date is recorded.
    if (this.useFoLtrSaveApi && this.isSignedCopyUpload) {
      this.docFields.push({
        key: 'signingDt',
        label: 'FACILITIES.AGREEMENT_DATE',
        type: 'date',
        validations: { required: true, noFuturDate: true },
      });
    }

    if (this.showUploadDateField) {
      this.docFields.push({
        key: 'uploadDt',
        label: 'DOCUMENT.UPLOAD_DATE',
        type: 'date',
        validations: {
          required: true,
          noFuturDate: true,
        },
      });
    }

    // "Charge Type" (ref code group 45) — asked once, only when the signed
    // copy of the Letter of Hypothecation (doc 21) is uploaded.
    if (this.useFoLtrSaveApi) {
      this.docFields.push({
        key: 'chrgTyp',
        label: 'DOCUMENT.CHARGE_TYPE',
        type: 'select',
        options$: this.pickListService.getOptions(PKL_GRP.CHARGE_TYPE),
        hidden: true,
      });
    }

    this.applyDocDateVisibility(this.docFormValue?.docTyp);
    this.applyChargeTypeVisibility(this.docFormValue?.docTyp);
  }

  /** Shows / requires "Charge Type" only for the LOH signed copy upload. */
  private applyChargeTypeVisibility(docTypSeq: any): void {
    if (!this.useFoLtrSaveApi) {
      return;
    }

    const index = this.docFields.findIndex((f) => f.key === 'chrgTyp');

    if (index === -1) {
      return;
    }

    // The row's `chrgTypReq` flag alone decides.
    const required = this.foLtrChrgTypReq;

    // New array so DynamicFormComponent rebuilds the form with the validator.
    this.docFields = this.docFields.map((field, i) =>
      i === index
        ? { ...field, hidden: !required, validations: required ? { required: true } : {} }
        : field,
    );
  }

  /**
   * Dropdown options for the upload form. With `hideUploadedDocTypes`, a type
   * already in the list stays out unless every row of it is rejected. The
   * type currently in the form (edit / pre-selected re-upload) always stays,
   * so the select never shows blank.
   */
  private getUploadDocTypeOptions(): any[] {
    const options =
      this.foLtrUploadGate && this.foLtrUploadableDocSeqs.length
        ? this.docTypeOptions.filter(
            (option: any) =>
              this.foLtrUploadableDocSeqs.includes(Number(option.value)) ||
              // A locked type always stays, so the disabled select never shows blank.
              Number(option.value) === this.lockedDocTyp,
          )
        : this.docTypeOptions;

    if (!this.hideUploadedDocTypes) {
      return options;
    }

    const blockedTypes = new Set(
      this.documents
        .filter((doc: any) => !this.isRejectedDocument(doc))
        .map((doc: any) => Number(doc.docTyp)),
    );

    const currentTyp = Number(this.docFormValue?.docTyp);

    return options.filter(
      (option: any) =>
        !blockedTypes.has(Number(option.value)) || Number(option.value) === currentTyp,
    );
  }

  /**
   * The status picklist has no fixed "rejected" code in the frontend, so the
   * label is matched. Resolved here (not from the row's cached label) because
   * the picklist can finish loading after the documents do.
   */
  private isRejectedDocument(doc: any): boolean {
    return this.getFoLtrStatusLabel(doc?.stsKey).trim().toLowerCase().includes('reject');
  }

  // =====================================
  // ISSUE DATE VISIBILITY
  // =====================================

  // Shows/requires the "Issue Date" field only for document types where
  // Document Types (setup) has "Issue Date Flag" (issuDtReqFlg) enabled.
  private applyDocDateVisibility(docTypSeq: any): void {
    const docDateFieldIndex = this.docFields.findIndex((f) => f.key === 'docDate');

    if (docDateFieldIndex === -1) {
      return;
    }

    const selected = this.docTypeOptions.find(
      (option) => Number(option.value) === Number(docTypSeq),
    );

    const required = !!selected?.issuDtReqFlg;

    // Facility Offer Letter (Approving Date): past and current dates only.
    // Leads / onboarding (Issue Date) keep accepting any date.
    const noFuturDate = this.useFoLtrSaveApi;

    // Reassign to a new array (not just mutate the field in place) so
    // DynamicFormComponent's ngOnChanges sees the `fields` input change and
    // rebuilds the form — otherwise the required validator never actually
    // attaches to the control, even though the field visually shows/hides.
    this.docFields = this.docFields.map((field, index) =>
      index === docDateFieldIndex
        ? {
            ...field,
            hidden: !required,
            validations: required
              ? { required: true, ...(noFuturDate ? { noFuturDate: true } : {}) }
              : {},
          }
        : field,
    );
  }

  // =====================================
  // LOAD DOCUMENTS
  // =====================================

  loadDocuments(): void {
    if (this.entitySeqs.length) {
      this.loadDocumentsForMany();
      return;
    }

    if (!this.entitySeq) {
      return;
    }

    // Facility Offer Letter "Required Documents" tab renders its own
    // checklist API (get-fo-ltr-issu-doc) — it never needs the "already
    // uploaded documents" list (get-fo-ltr-doc), so skip that call entirely.
    if (this.showRequiredDocChecklist) {
      this.documents = [];
      this.documentsChange.emit(this.documents);
      this.loadRequiredDocChecklist();
      return;
    }

    // FOL new flow: there is no FOL approval chain any more (vcm-aprv /
    // vcm-rjct were removed), so every role — the Credit Manager included —
    // lists the letter's own documents from get-fo-ltr-doc.
    const useFoLtrDocuments = this.useFoLtrSaveApi;

    const request$ = useFoLtrDocuments
      ? this.creditService.getFoLtrDocuments(this.entitySeq)
      : this.leadService.getLeadDocs(this.entitySeq, this.moduleSeq);

    // GET /crdt-prpsl/fo-ltr/get-fo-ltr-doc names its rows differently (see mapDocRow below).
    const usesFoLtrDocSeqNaming = useFoLtrDocuments;

    request$.subscribe({
      next: (res: any) => {
        // get-fo-ltr-doc now returns { canUpload, uploadableDocSeqs, canSubmit, docs }.
        const rawList = Array.isArray(res)
          ? res
          : Array.isArray(res?.docs)
            ? res.docs
            : Array.isArray(res?.data)
              ? res.data
              : [];

        if (useFoLtrDocuments) {
          this.foLtrCanUpload = res?.canUpload === true;
          this.foLtrUploadableDocSeqs = Array.isArray(res?.uploadableDocSeqs)
            ? res.uploadableDocSeqs.map(Number)
            : [];

          this.foLtrDocFlagsChange.emit({
            canUpload: res?.canUpload === true,
            uploadableDocSeqs: Array.isArray(res?.uploadableDocSeqs)
              ? res.uploadableDocSeqs.map(Number)
              : [],
            canSubmit: res?.canSubmit === true,
          });
        }

        const list = rawList;

        this.documents = list
          .filter((x: any) => !x.delFlg)
          .map((doc: any) => this.mapDocRow(doc, usesFoLtrDocSeqNaming))
          .filter(
            (doc: any) =>
              !this.visibleDocSeqs.length || this.visibleDocSeqs.includes(Number(doc.docTyp)),
          );

        this.documentsChange.emit(this.documents);
      },

      error: () => {
        this.documentsChange.emit([]);

        if (useFoLtrDocuments) {
          this.foLtrCanUpload = false;
          this.foLtrUploadableDocSeqs = [];
          this.foLtrDocFlagsChange.emit({ canUpload: false, uploadableDocSeqs: [], canSubmit: false });
        }

        this.toastr.error(this.translate.instant('DOCUMENT.FAILED_TO_LOAD_DOCUMENTS'));
      },
    });
  }

  /**
   * Loads and merges the documents of every entity in `entitySeqs`.
   *
   * One request per entity — the backend has no batch endpoint — with each
   * failure swallowed so a single bad record cannot blank the whole table.
   */
  private loadDocumentsForMany(): void {
    const seqs = [...new Set(this.entitySeqs.map(Number).filter((seq) => seq > 0))];

    if (!seqs.length) {
      this.documents = [];
      this.documentsChange.emit(this.documents);
      return;
    }

    forkJoin(
      seqs.map((seq) =>
        this.leadService.getLeadDocs(seq, this.moduleSeq).pipe(catchError(() => of([]))),
      ),
    ).subscribe((responses: any[]) => {
      this.documents = responses.flatMap((res, index) => {
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];

        return list
          .filter((x: any) => !x.delFlg)
          .map((doc: any) => ({
            ...this.mapDocRow(doc),

            // mapDocRow falls back to `entitySeq`, which is unset in this
            // mode, so the owning record is carried over from the request.
            entySeq: Number(doc.entySeq ?? seqs[index]),
          }));
      });

      this.documentsChange.emit(this.documents);
    });
  }

  private mapDocRow(doc: any, usesFoLtrDocSeqNaming = false): any {
    // vcm-pending-lst and get-fo-ltr-doc both name the uploaded-document row
    // `crdtFoLtrDocSeq` (used for approve/reject/delete) and the document
    // *type* `docSeq` — everywhere else those are `entyDocSeq` and `docTyp`
    // respectively.
    const docSeq = usesFoLtrDocSeqNaming ? doc.crdtFoLtrDocSeq : (doc.entyDocSeq ?? doc.docSeq);

    const docTyp = usesFoLtrDocSeqNaming ? doc.docSeq : doc.docTyp;

    return {
      docSeq,

      entySeq: Number(doc.entySeq ?? this.entitySeq),

      entyTyp: Number(doc.entyTyp ?? doc.entyType ?? this.moduleSeq),

      docNm: doc.docNm,

      docTyp,

      docTypLabel: this.getDocumentTypeLabel(docTyp),

      docDesc: doc.docDesc,

      docDate: doc.issuanceDt ?? doc.aprvngDt ?? doc.docDate,

      docDateLabel:
        (doc.issuanceDt ?? doc.aprvngDt ?? doc.docDate)
          ? new Date(doc.issuanceDt ?? doc.aprvngDt ?? doc.docDate).toLocaleDateString()
          : '',

      chrgTyp: doc.chrgTyp,

      uploadDt: doc.uploadDt,

      // Facility Offer Letter rows (get-fo-ltr-doc) only — undefined elsewhere.
      crdtFoLtrDocSeq: usesFoLtrDocSeqNaming ? (doc.crdtFoLtrDocSeq ?? null) : undefined,

      sftCpyDt: doc.sftCpyDt,

      hrdCpyDt: doc.hrdCpyDt,

      sgndCpyFlg: doc.sgndCpyFlg,

      docDt: doc.docDt,

      visitDateLabel: doc.docDt ? new Date(doc.docDt).toLocaleDateString() : '-',

      stsKey: doc.stsKey,

      statusLabel: this.getFoLtrStatusLabel(doc.stsKey),

      docImg: doc.docImg,

      updatedDate: this.formatDate(doc.lastUpdDt || doc.crtdDt),
    };
  }

  // =====================================
  // GET DOC LABEL
  // =====================================

  /** Every document type of this module, by `docSeq` — unaffected by the dropdown filters. */
  private docTypeLabels = new Map<number, string>();

  getDocumentTypeLabel(value: number): string {
    const found = this.docTypeOptions.find((x: any) => x.value === value);

    return found?.label || this.docTypeLabels.get(Number(value)) || '-';
  }

  /**
   * Facility Offer Letter document status — `stsKey` isn't confined to one
   * ref code group, so both groups used across this flow (23, 26) are
   * scanned in order. Used by the "All Documents" and "Required Documents"
   * tabs only.
   */
  /** Re-resolves the Status column once the status picklists have loaded. */
  private refreshFoLtrStatusLabels(): void {
    const relabel = (row: any) => ({ ...row, statusLabel: this.getFoLtrStatusLabel(row?.stsKey) });

    this.documents = this.documents.map(relabel);
    this.requiredDocChecklist = this.requiredDocChecklist.map(relabel);
  }

  private getFoLtrStatusLabel(stsKey: any): string {
    if (stsKey == null) {
      return '-';
    }

    // Ref code group 50 (FOL statuses) first; 23 / 26 as before.
    const statusGroups = [PKL_GRP.FOL_STATUS, PKL_GRP.ONBOARDING_STATUS, PKL_GRP.PANDDING_STATUS];

    for (const group of statusGroups) {
      const option = this.pickListService
        .getOptionsSync(group)
        .find((item) => Number(item.value) === Number(stsKey));

      if (option) {
        return option.label;
      }
    }

    // The FOL new-flow codes (4810–4816) may not be in either group yet.
    const fallbackKey = FO_LTR_STATUS_LABEL_KEYS[Number(stsKey)];

    return fallbackKey ? this.translate.instant(fallbackKey) : String(stsKey);
  }

  // =====================================
  // FORMAT DATE
  // =====================================

  formatDate(value: string): string {
    if (!value) {
      return '';
    }

    return new Date(value).toLocaleString();
  }

  /** `yyyy-MM-dd` for today — what the save-doc API's LocalDate fields expect. */
  private todayIsoDate(): string {
    const now = new Date();

    const month = String(now.getMonth() + 1).padStart(2, '0');

    const day = String(now.getDate()).padStart(2, '0');

    return `${now.getFullYear()}-${month}-${day}`;
  }

  // =====================================
  // FORM CHANGE
  // =====================================

  /**
   * Document type fixed by the Required Documents row the upload was opened
   * from — the select is disabled, so it is not part of the form's value and
   * is put back here.
   */
  private lockedDocTyp: number | null = null;

  private withLockedDocTyp(value: any): any {
    return this.lockedDocTyp != null ? { ...value, docTyp: this.lockedDocTyp } : value;
  }

  onDocFormChange(value: any): void {
    value = this.withLockedDocTyp(value);

    this.docFormValue = value;
    this.applyDocDateVisibility(value?.docTyp);
    this.applyChargeTypeVisibility(value?.docTyp);
  }

  // =====================================
  // OPEN MODAL
  // =====================================

  /**
   * Facility Offer Letter checklist upload kind: a normal (first) upload, the
   * signed copy of a 4813 row, or a re-upload of a 4814 row.
   */
  foLtrUploadMode: 'normal' | 'signedCopy' | 'reupload' = 'normal';
  private foLtrUploadRow: any = null;
  /** Checklist row the upload was opened from (any mode) — carries the upload flags. */
  private foLtrFlagsRow: any = null;

  /** `canUploadSgndCpy` true → sgndCpyFlg 1, otherwise 0. */
  private get foLtrSgndCpyFlg(): 0 | 1 {
    return this.foLtrFlagsRow?.canUploadSgndCpy === true ? 1 : 0;
  }

  /** `chrgTypReq` true → Charge Type is asked in the popup. */
  private get foLtrChrgTypReq(): boolean {
    return this.foLtrFlagsRow?.chrgTypReq === true;
  }

  /** The upload is (or replaces) a signed copy — it also records the agreement date. */
  get isSignedCopyUpload(): boolean {
    return (
      this.foLtrUploadMode === 'signedCopy' ||
      (this.foLtrUploadMode === 'reupload' && Number(this.foLtrUploadRow?.sgndCpyFlg ?? 0) === 1)
    );
  }

  get uploadModalTitle(): string {
    if (this.foLtrUploadMode === 'signedCopy') {
      return 'FACILITIES.UPLOAD_SIGNED_COPY';
    }

    if (this.foLtrUploadMode === 'reupload') {
      return 'FACILITIES.REUPLOAD_DOCUMENT';
    }

    return this.editingDocSeq ? 'DOCUMENT.EDIT_DOCUMENT' : 'DOCUMENT.UPLOAD_DOCUMENT';
  }

  openUploadModal(
    docTyp?: number,
    crdtFoLtrDocSeq?: number | null,
    checklistRow?: any,
    mode: 'normal' | 'signedCopy' | 'reupload' = 'normal',
  ): void {
    if (this.isViewMode) {
      return;
    }

    // A previous checklist upload may have left the type select disabled.
    const wasLocked = this.docFields.some((f) => f.key === 'docTyp' && f.disabled);

    this.resetForm();

    this.foLtrUploadMode = mode;
    this.foLtrUploadRow = mode === 'normal' ? null : checklistRow;
    this.foLtrFlagsRow = checklistRow ?? null;

    // Only one type can be uploaded here: select it and lock the dropdown.
    // Counted on the allowed types (`allowedDocSeqs`), not the backend's
    // `uploadableDocSeqs`, which may not list it and would skip the lock.
    if (!docTyp && mode === 'normal' && this.lockSingleDocType && this.docTypeOptions.length === 1) {
      docTyp = Number(this.docTypeOptions[0].value);
    }

    // Opened from a Required Documents checklist row — pre-select its type
    // and, when the checklist already returned one, its crdtFoLtrDocSeq
    // (the API requires this key present even on this "first" upload).
    if (docTyp) {
      this.lockedDocTyp = Number(docTyp);

      this.docFormValue = {
        docTyp,
        ...(this.showFoLtrDateFields
          ? {
              sftCpyDt: checklistRow?.sftCpyDt ?? null,
              hrdCpyDt: checklistRow?.hrdCpyDt ?? null,
            }
          : {}),
        // Charge type: asked only when the row's chrgTypReq is true.
        ...(this.useFoLtrSaveApi && this.foLtrChrgTypReq
          ? { chrgTyp: checklistRow?.chrgTyp ?? null }
          : {}),
        ...(mode === 'reupload' ? { signingDt: checklistRow?.signingDt ?? null } : {}),
      };
      this.applyDocDateVisibility(docTyp);
      this.applyChargeTypeVisibility(docTyp);

      if (crdtFoLtrDocSeq) {
        this.editingDocSeq = crdtFoLtrDocSeq;
      }
    }

    // Rebuild so the type lock applies, and so a filtered dropdown reflects
    // the documents as they are right now (and the agreement date shows for
    // a signed copy).
    if (this.hideUploadedDocTypes || this.lockedDocTyp != null || wasLocked || mode !== 'normal') {
      this.buildDocFields();
    }

    this.showUploadModal = true;
  }

  // =====================================
  // CLOSE MODAL
  // =====================================

  closeUploadModal(): void {
    this.resetForm();

    this.showUploadModal = false;
  }

  // =====================================
  // RESET
  // =====================================

  resetForm(): void {
    this.editingDocSeq = null;

    this.foLtrUploadMode = 'normal';

    this.foLtrUploadRow = null;

    this.foLtrFlagsRow = null;

    this.lockedDocTyp = null;

    this.docFormValue = {};

    this.selectedFile = null;

    this.previewUrl = null;

    this.fileError = '';

    this.applyDocDateVisibility(null);
    this.applyChargeTypeVisibility(null);
  }

  // =====================================
  // FILE SELECT
  // =====================================

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    this.fileError = '';

    if (this.allowedExtensions.length) {
      const extension = (file.name.split('.').pop() ?? '').toLowerCase();

      if (!this.allowedExtensions.includes(extension)) {
        // Clear the input so re-picking the same file still raises a change.
        input.value = '';

        this.fileError = this.translate.instant('DOCUMENT.ONLY_EXTENSIONS_ALLOWED', {
          extensions: this.allowedExtensions.map((value) => '.' + value).join(', '),
        });

        return;
      }
    }

    // Images are compressed first (re-encoded at a smaller size/quality),
    // then the 25MB cap is checked against the final file that would
    // actually be uploaded.
    this.compressingFile = true;

    let fileToUpload = file;

    try {
      if (file.type.startsWith('image/')) {
        fileToUpload = await this.compressImageFile(file);
      }
    } catch (error) {
      console.error('Image compression failed, using original file:', error);
    } finally {
      this.compressingFile = false;

      // The Image/canvas callbacks driving compression aren't always
      // picked up by Angular's zone, so force a view refresh here —
      // otherwise the popup keeps showing the upload box until some
      // unrelated click forces the next change-detection pass.
      this.cdr.detectChanges();
    }

    if (fileToUpload.size > this.maxFileSizeBytes) {
      this.fileError = this.translate.instant('DOCUMENT.FILE_SIZE_EXCEEDED');

      input.value = '';

      return;
    }

    this.selectedFile = fileToUpload;

    const reader = new FileReader();

    reader.onload = () => {
      this.previewUrl = reader.result as string;
      this.cdr.detectChanges();
    };

    reader.readAsDataURL(fileToUpload);
  }

  // =====================================
  // IMAGE COMPRESSION (canvas re-encode — no external library)
  // =====================================

  private compressImageFile(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        URL.revokeObjectURL(objectUrl);

        const scale = Math.min(1, this.maxImageDimension / Math.max(image.width, image.height));

        const targetWidth = Math.round(image.width * scale);
        const targetHeight = Math.round(image.height * scale);

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const context = canvas.getContext('2d');

        if (!context) {
          resolve(file);
          return;
        }

        context.drawImage(image, 0, 0, targetWidth, targetHeight);

        // PNG stays PNG (canvas ignores quality for lossless formats) — the
        // downscale above still shrinks it. Everything else re-encodes as
        // JPEG at reduced quality for the biggest size win.
        const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const quality = outputType === 'image/jpeg' ? 0.7 : undefined;

        canvas.toBlob(
          (blob) => {
            if (!blob || blob.size >= file.size) {
              resolve(file);
              return;
            }

            const compressedName =
              outputType === 'image/jpeg'
                ? `${this.getFileNameWithoutExtension(file.name)}.jpg`
                : file.name;

            resolve(new File([blob], compressedName, { type: outputType }));
          },
          outputType,
          quality,
        );
      };

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Unable to load image for compression'));
      };

      image.src = objectUrl;
    });
  }

  // =====================================
  // SUBMIT
  // =====================================

  triggerDynamicSubmit(): void {
    this.dynamicForm?.submit();
  }
  // =====================================
  // SAVE
  // =====================================

  handleDynamicSubmit(formValue: any): void {
    formValue = this.withLockedDocTyp(formValue);

    if (!this.selectedFile && !this.previewUrl) {
      this.fileError = this.translate.instant('DOCUMENT.FILE_REQUIRED');

      return;
    }

    if (this.useFoLtrSaveApi) {
      this.saveFoLtrDocument(formValue);
      return;
    }

    const formData = new FormData();

    if (this.selectedFile) {
      formData.append('docImg', this.selectedFile);
    }

    if (this.editingDocSeq) {
      formData.append('entyDocSeq', this.editingDocSeq.toString());
    }

    formData.append('entySeq', this.entitySeq.toString());

    formData.append('entyTyp', this.moduleSeq.toString());

    formData.append('docTyp', formValue.docTyp);

    // formData.append('docNm', formValue.docNm || '');

    // formData.append('docDesc', formValue.docDesc || '');

    formData.append('issuanceDt', formValue.docDate || '');

    if (this.docDt) {
      formData.append('docDt', this.docDt);
    }

    formData.append('delFlg', 'false');

    this.leadService.saveLeadDocs(formData).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('DOCUMENT.SAVED_SUCCESSFULLY'));

        this.closeUploadModal();

        this.loadDocuments();
      },

      error: () => {
        this.toastr.error(this.translate.instant('DOCUMENT.SAVE_FAILED'));
      },
    });
  }

  /** POST /crdt-prpsl/fo-ltr/save-doc — Facility Offer Letter document upload. */
  private saveFoLtrDocument(formValue: any): void {
    // Signed copy: its own payload (see buildFoLtrSignedCopyFormData).
    if (this.isSignedCopyUpload) {
      const crdtFoLtrDocSeq = Number(this.foLtrUploadRow?.crdtFoLtrDocSeq ?? 0);

      if (crdtFoLtrDocSeq <= 0 || !this.selectedFile) {
        this.toastr.warning(this.translate.instant('DOCUMENT.DOCUMENT_INFORMATION_MISSING'));
        return;
      }

      this.postFoLtrDocument(
        buildFoLtrSignedCopyFormData({
          crdtFoLtrSeq: this.entitySeq,
          docSeq: Number(formValue.docTyp),
          crdtFoLtrDocSeq,
          sftCpyDt: formValue.sftCpyDt,
          hrdCpyDt: formValue.hrdCpyDt,
          signingDt: formValue.signingDt,
          chrgTyp: formValue.chrgTyp,
          chrgTypReq: this.foLtrChrgTypReq,
          sgndCpyFlg: this.foLtrSgndCpyFlg,
          file: this.selectedFile,
        }),
      );
      return;
    }

    const formData = new FormData();

    formData.append('crdtFoLtrSeq', this.entitySeq.toString());

    formData.append('docSeq', formValue.docTyp);

    // `docNm` is deliberately absent: the document name is the backend's to
    // decide, and it comes back on the GET, where this screen just displays it.

    formData.append('docDesc', formValue.docDesc || '');

    // save-doc rejects the request outright without this one ("aprvngDt is
    // required"), so it is always sent.
    formData.append('aprvngDt', formValue.docDate || '');

    formData.append('uploadDt', formValue.uploadDt || this.todayIsoDate());

    // Facility Offer Letter Documents page only — the other screens never show
    // these fields, so their payload stays exactly as it was. An unset date is
    // left out rather than sent as an empty string.
    if (this.showFoLtrDateFields) {
      if (formValue.sftCpyDt) {
        formData.append('sftCpyDt', formValue.sftCpyDt);
      }

      if (formValue.hrdCpyDt) {
        formData.append('hrdCpyDt', formValue.hrdCpyDt);
      }
    }

    // Checklist row flags: canUploadSgndCpy → sgndCpyFlg 1 / 0, and the
    // Charge Type picked in the popup when chrgTypReq is true.
    formData.append('sgndCpyFlg', String(this.foLtrSgndCpyFlg));

    if (this.foLtrChrgTypReq && formValue.chrgTyp != null && formValue.chrgTyp !== '') {
      formData.append('chrgTyp', String(formValue.chrgTyp));
    }

    formData.append('delFlg', '0');

    // Re-upload of a type that replaces its existing row (e.g. the printed
    // copy of the offer letter, doc 36) carries that row's seq.
    const replacedDocSeq = this.editingDocSeq ?? this.findReplacedFoLtrDocSeq(formValue.docTyp);

    // Backend binds this to a Java Integer — it can't parse the string
    // "null", so create simply omits the key; edit sends the actual seq.
    if (replacedDocSeq) {
      formData.append('crdtFoLtrDocSeq', replacedDocSeq.toString());
    }


    if (this.selectedFile) {
      formData.append('file', this.selectedFile);
    }

    this.creditService.saveFoLtrDoc(formData).subscribe({
      next: (res: any) => {
        // save-doc can return 200 with an error body; ErrorInterceptor already
        // toasts it and tags the body `__hasError` — keep the modal open, no success toast.
        if (res?.__hasError) {
          return;
        }

        this.toastr.success(this.translate.instant('DOCUMENT.SAVED_SUCCESSFULLY'));

        this.closeUploadModal();

        this.loadDocuments();

        this.foLtrDocSaved.emit();
      },

      error: () => {
        this.toastr.error(this.translate.instant('DOCUMENT.SAVE_FAILED'));
      },
    });
  }

  /** POST /crdt-prpsl/fo-ltr/save-doc with a ready payload (signed copy). */
  private postFoLtrDocument(formData: FormData): void {
    this.creditService.saveFoLtrDoc(formData).subscribe({
      next: (res: any) => {
        // 200 with an error body: the interceptor already toasted it — keep the modal open.
        if (res?.__hasError) {
          return;
        }

        if (res?.error) {
          this.toastr.error(res.error);
          return;
        }

        this.toastr.success(this.translate.instant('DOCUMENT.SAVED_SUCCESSFULLY'));

        this.closeUploadModal();

        this.loadDocuments();

        this.foLtrDocSaved.emit();
      },

      error: (err: any) => {
        this.toastr.error(
          err?.error?.message ?? err?.error?.error ?? this.translate.instant('DOCUMENT.SAVE_FAILED'),
        );
      },
    });
  }

  /** The existing row a `replaceExistingDocSeqs` type upload replaces, if any. */
  private findReplacedFoLtrDocSeq(docTyp: any): number | null {
    if (!this.replaceExistingDocSeqs.includes(Number(docTyp))) {
      return null;
    }

    const existing = this.documents.find((doc: any) => Number(doc.docTyp) === Number(docTyp));

    return existing?.docSeq ? Number(existing.docSeq) : null;
  }

  /** POST /crdt-prpsl/fo-ltr/save-doc — Facility Offer Letter document delete (delFlg=1, existing crdtFoLtrDocSeq). */
  private deleteFoLtrDocument(row: any): void {
    const formData = new FormData();

    formData.append('crdtFoLtrSeq', this.entitySeq.toString());

    formData.append('docSeq', row.docTyp);

    // formData.append('docNm', row.docNm || '');

    // formData.append('docDesc', row.docDesc || '');

    // Checklist rows carry the type as docTyp and the row as crdtFoLtrDocSeq.
    formData.append('crdtFoLtrDocSeq', String(row.crdtFoLtrDocSeq ?? row.docSeq));

    formData.append('aprvngDt', row.docDate || '');

    if (row.chrgTyp) {
      formData.append('chrgTyp', row.chrgTyp);
    }

    // Deleting an LOH signed copy also deletes its charge row (backend).
    formData.append('delFlg', '1');

    const docFile = this.base64ToFile(
      row.docImg,
      this.getFileNameWithoutExtension(row.docNm || 'document'),
    );

    if (docFile) {
      formData.append('file', docFile);
    }

    this.creditService.saveFoLtrDoc(formData).subscribe({
      next: (res: any) => {
        // Same 200-with-error case as saveFoLtrDocument; the interceptor already toasted it.
        if (res?.__hasError) {
          return;
        }

        this.toastr.success(this.translate.instant('DOCUMENT.DELETED_SUCCESSFULLY'));

        this.loadDocuments();

        this.foLtrDocSaved.emit();
      },

      error: () => {
        this.toastr.error(this.translate.instant('DOCUMENT.DELETE_FAILED'));
      },
    });
  }

  // =====================================
  // EDIT
  // =====================================

  editDocument(row: any): void {
    if (this.isViewMode) {
      return;
    }

    this.editingDocSeq = row.docSeq;

    this.docFormValue = {
      docTyp: row.docTyp,
      docNm: row.docNm,
      docDesc: row.docDesc,
      docDate: row.docDate,
      chrgTyp: row.chrgTyp,
      uploadDt: row.uploadDt,
    };

    if (this.hideUploadedDocTypes) {
      // Keeps the edited row's own type in the (filtered) dropdown.
      this.buildDocFields();
    } else {
      this.applyDocDateVisibility(row.docTyp);
      this.applyChargeTypeVisibility(row.docTyp);
    }

    const cleanBase64 = this.cleanBase64(row.docImg);
    const { mimeType } = this.getFileInfo(cleanBase64, row.docNm);

    this.previewUrl =
      cleanBase64 && mimeType.startsWith('image/')
        ? `data:${mimeType};base64,${cleanBase64}`
        : null;

    this.selectedFile = null;
    this.showUploadModal = true;
  }

  // =====================================
  // DELETE CONFIRM
  // =====================================

  openDeletePopup(row: any): void {
    this.selectedRow = row;
    this.deleteMessage = this.translate.instant('DOCUMENT.CONFIRM_DELETE_DOCUMENT', { name: row.docNm });
    this.showDeletePopup = true;
  }

  confirmDelete(): void {
    if (!this.selectedRow) {
      return;
    }

    this.deleteDocument(this.selectedRow);
    this.selectedRow = null;
  }

  // =====================================
  // DELETE
  // =====================================

  deleteDocument(row: any): void {
    if (this.useFoLtrSaveApi) {
      this.deleteFoLtrDocument(row);
      return;
    }

    const formData = new FormData();

    formData.append('entyDocSeq', String(row.docSeq));

    formData.append('entySeq', this.entitySeq.toString());

    formData.append('entyTyp', this.moduleSeq.toString());

    formData.append('docTyp', row.docTyp);

    // formData.append('docNm', row.docNm || '');

    // formData.append('docDesc', row.docDesc || '');

    formData.append('issuanceDt', row.docDate || '');

    const docFile = this.base64ToFile(
      row.docImg,
      this.getFileNameWithoutExtension(row.docNm || 'document'),
    );

    if (docFile) {
      formData.append('docImg', docFile);
    }

    formData.append('delFlg', 'true');

    this.leadService.saveLeadDocs(formData).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('DOCUMENT.DELETED_SUCCESSFULLY'));

        this.loadDocuments();
      },

      error: () => {
        this.toastr.error(this.translate.instant('DOCUMENT.DELETE_FAILED'));
      },
    });
  }

  // =====================================
  // DOWNLOAD
  // =====================================

  private base64ToFile(base64Value: string, fileNameWithoutExtension: string): File | null {
    const cleanBase64 = this.cleanBase64(base64Value);

    if (!cleanBase64) {
      return null;
    }

    const { mimeType, extension } = this.getFileInfo(cleanBase64);

    if (extension === 'bin') {
      return null;
    }

    const byteCharacters = atob(cleanBase64);
    const byteArray = new Uint8Array(byteCharacters.length);

    for (let index = 0; index < byteCharacters.length; index++) {
      byteArray[index] = byteCharacters.charCodeAt(index);
    }

    return new File([byteArray], `${fileNameWithoutExtension}.${extension}`, {
      type: mimeType,
    });
  }

  /** Download action: lets the screen's `beforeDownload` hook handle it first, if any. */
  async onDownloadClick(row: any): Promise<void> {
    if (this.beforeDownload) {
      try {
        if (await this.beforeDownload(row)) {
          return;
        }
      } catch (error) {
        console.error('Before-download step failed:', error);
      }
    }

    this.downloadFile(row);
  }

  downloadFile(row: any): void {
    if (!row?.docImg) {
      this.toastr.warning(this.translate.instant('DOCUMENT.NO_FILE_AVAILABLE'));
      return;
    }

    try {
      const cleanBase64 = this.cleanBase64(row.docImg);

      if (!cleanBase64) {
        this.toastr.error(this.translate.instant('DOCUMENT.INVALID_DOCUMENT_DATA'));
        return;
      }

      const { mimeType, extension } = this.getFileInfo(cleanBase64, row?.docNm);

      if (extension === 'bin') {
        console.error('Unknown Base64 signature:', cleanBase64.substring(0, 30));

        this.toastr.error(this.translate.instant('DOCUMENT.UNABLE_TO_IDENTIFY_FILE_FORMAT'));
        return;
      }

      const byteCharacters = atob(cleanBase64);
      const byteArray = new Uint8Array(byteCharacters.length);

      for (let index = 0; index < byteCharacters.length; index++) {
        byteArray[index] = byteCharacters.charCodeAt(index);
      }

      const blob = new Blob([byteArray], {
        type: mimeType,
      });

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');

      const baseFileName = this.getFileNameWithoutExtension(row.docNm || 'document');

      link.href = objectUrl;
      link.download = `${baseFileName}.${extension}`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
      }, 1000);
    } catch (error) {
      console.error('Document download failed:', error);
      this.toastr.error(this.translate.instant('DOCUMENT.UNABLE_TO_DOWNLOAD_DOCUMENT'));
    }
  }
}
